import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { EntrataSession } from 'auth/EntrataSession';
import i18n from 'i18n';
import { RootState } from 'state/store';

import { getViewerPropertySystemTranslations, getViewerTranslations } from 'services/translations';

const DEFAULT_LOCALE = 'en-US';

export function useViewerTranslations(localeCode: string = DEFAULT_LOCALE) {
  const { applicationId, versionId, propertyId } = useSelector((state: RootState) => {
    const { form, activeResponse, applicant } = state.application;
    const { leadProfile } = state;

    // Application entity ID — used as the API path param.
    // Check multiple sources since applicant/currentResponse may not be set on all pages (e.g. Entry).
    let appId: string | undefined;
    if (form?.applicationId) {
      appId = String(form.applicationId);
    } else if (leadProfile?.applicationForm?.applicationId) {
      appId = String(leadProfile.applicationForm.applicationId);
    } else if (applicant?.currentResponse?.applicationId) {
      appId = String(applicant.currentResponse.applicationId);
    } else if (activeResponse?.applicationId) {
      appId = String(activeResponse.applicationId);
    }

    // Version ID — tells the API which exact form version the viewer is rendering.
    const ver =
      applicant?.currentResponse?.versionId ||
      activeResponse?.versionId ||
      leadProfile?.applicationForm?.versionId ||
      undefined;

    // Property ID — available on pre-application screens (login, signup, entry)
    // before the user has selected an application.
    // Falls back to state.locale.propertyId which LanguageSwitcher sets from URL params
    // so that property translations are fetched even before the applicant logs in.
    const propId = applicant?.propertyId
      ? Number(applicant.propertyId)
      : (state.locale.propertyId ?? undefined);

    return { applicationId: appId, versionId: ver, propertyId: propId };
  }, shallowEqual);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (EntrataSession.sessionExpired) {
        return;
      }

      try {
        let translations: Record<string, string> = {};

        if (applicationId) {
          // User is in an application context — fetch both form and system translations.
          const viewerTranslations = await getViewerTranslations(
            applicationId,
            localeCode,
            versionId,
          );
          translations = {
            ...(viewerTranslations.form?.translations ?? {}),
            ...(viewerTranslations.system?.translations ?? {}),
          };
        } else if (propertyId) {
          // Pre-application screen (login, signup, forgot-password, entry) —
          // fetch property-scoped system translations only.
          translations = await getViewerPropertySystemTranslations(propertyId, localeCode);
        }

        if (cancelled) return;

        // Only add a resource bundle when there are actual overrides — but always
        // switch the language so i18next falls back to default strings when
        // switching back to English (or any locale with no stored translations).
        if (Object.keys(translations).length > 0) {
          const cleanTranslations = Object.fromEntries(
            Object.entries(translations).filter(
              (entry): entry is [string, string] =>
                entry[1] != null && typeof entry[1] === 'string',
            ),
          );
          i18n.addResourceBundle(localeCode, 'viewer', cleanTranslations, true, true);
        }

        // Force language change event (i18next no-ops when already on same locale).
        // Round-trip ensures components using useTranslation re-render with new resources.
        const currentLng = i18n.language;
        if (currentLng === localeCode) {
          await i18n.changeLanguage('en-US');
        }
        await i18n.changeLanguage(localeCode);
      } catch {
        // On error fall through to defaults — don't block the UI.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applicationId, versionId, propertyId, localeCode]);
}
